/** Liste stylisée — même geste que les dropdowns MCV, charte FacturX. */
import { useEffect, useMemo, useRef, useState } from "react";
import "./fxCtrl.css";
import { SearchClear } from "./SearchClear";

export type FxOption = { value: string; label: string; search?: string };

type FxSelectProps = {
  id?: string;
  value: string;
  options: FxOption[];
  placeholder?: string;
  disabled?: boolean;
  compact?: boolean;
  plain?: boolean;
  searchable?: boolean;
  onChange: (value: string) => void;
};

export function FxSelect({
  id,
  value,
  options,
  placeholder = "— Choisir —",
  disabled,
  compact,
  plain,
  searchable,
  onChange,
}: FxSelectProps) {
  const wrap = useRef<HTMLDivElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const qRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });

  function place() {
    const r = btn.current?.getBoundingClientRect();
    if (!r) return;
    setPos({ top: r.bottom + 6, left: r.left, width: Math.max(r.width, 180) });
  }

  useEffect(() => {
    if (!open) return;
    setQ("");
    place();
    const t = window.setTimeout(() => qRef.current?.focus(), 20);
    function onDoc(e: MouseEvent) {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  const current = options.find((o) => o.value === value);
  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return options;
    return options.filter((o) => `${o.label} ${o.search || ""}`.toLowerCase().includes(s));
  }, [options, q]);

  return (
    <div
      ref={wrap}
      className={["fx-ctrl fx-sel", open ? "is-open" : "", compact ? "is-compact" : "", plain ? "is-plain" : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        ref={btn}
        id={id}
        type="button"
        className="fx-ctrl__btn"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((v) => !v)}
      >
        {plain ? null : (
          <span className="fx-ctrl__ico" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
              <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
            </svg>
          </span>
        )}
        <span className={current ? "fx-ctrl__lab" : "fx-ctrl__lab is-empty"}>{current?.label || placeholder}</span>
        <span className="fx-ctrl__chev" aria-hidden>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>
      {open ? (
        <div className="fx-ctrl__panel fx-sel__panel" role="listbox" style={{ top: pos.top, left: pos.left, width: pos.width }}>
          {searchable ? (
            <div className="fx-sel__qwrap">
              <input
                ref={qRef}
                className="fx-sel__q"
                value={q}
                placeholder="Rechercher…"
                onChange={(e) => setQ(e.target.value)}
              />
              <SearchClear value={q} onClear={() => setQ("")} />
            </div>
          ) : null}
          {visible.length === 0 ? <p className="fx-sel__empty">Aucun résultat.</p> : null}
          {visible.map((o) => (
            <button
              key={o.value || "empty"}
              type="button"
              role="option"
              aria-selected={o.value === value}
              className={o.value === value ? "fx-sel__opt is-on" : "fx-sel__opt"}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
